import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { WhatsAppFAB } from '../../components/WhatsAppFAB';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { UserHeader } from '../../components/UserHeader';
import {
  Upload, Send, CheckCircle, AlertCircle, Scale,
  Download, ArrowLeft, RefreshCw, Loader2, Info, CalendarIcon,
  MapPin, ExternalLink, Mail, Archive, ChevronDown, ChevronRight, FileText,
} from 'lucide-react';
import { format, parse } from 'date-fns';
import axios from 'axios';
import { goBack } from '../../utils/navigation';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// ── Historial de inscripciones (Opción A: dentro de /auxiliares-justicia) ──
const ESTADO_META_HIST = {
  enviada: { label: 'Enviada', color: 'bg-blue-100 text-blue-800' },
  enviado: { label: 'Enviada', color: 'bg-blue-100 text-blue-800' },
  aprobada: { label: 'Aprobada', color: 'bg-green-100 text-green-800' },
  observada: { label: 'Observada', color: 'bg-amber-100 text-amber-800' },
  rechazada: { label: 'Rechazada', color: 'bg-red-100 text-red-800' },
  pendiente_nueva_revision: { label: 'Pendiente revisión', color: 'bg-purple-100 text-purple-800' },
};

function HistorialInscripciones({ token, apiUrl }) {
  const [loading, setLoading] = React.useState(true);
  const [porAnio, setPorAnio] = React.useState([]);
  const [expandido, setExpandido] = React.useState({});

  React.useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${apiUrl}/auxiliares-justicia/mis-solicitudes`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const sols = Array.isArray(res.data) ? res.data : [];
        // Agrupar por anio_actuacion (desc)
        const map = {};
        for (const s of sols) {
          const y = s.anio_actuacion || (s.fecha_envio ? new Date(s.fecha_envio).getFullYear() : 'Sin año');
          map[y] = map[y] || [];
          map[y].push(s);
        }
        const arr = Object.entries(map)
          .map(([anio, items]) => ({ anio, items }))
          .sort((a, b) => Number(b.anio) - Number(a.anio));
        setPorAnio(arr);
        // Expandimos el año más reciente por default
        if (arr.length) setExpandido({ [arr[0].anio]: true });
      } catch (e) {
        // silencioso, tabla vacía
      } finally {
        setLoading(false);
      }
    })();
  }, [token, apiUrl]);

  if (loading) {
    return (
      <Card className="mt-6" data-testid="historial-inscripciones-loading">
        <CardContent className="py-8 text-center text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
          Cargando historial…
        </CardContent>
      </Card>
    );
  }
  if (!porAnio.length) return null;

  return (
    <Card className="mt-6" data-testid="historial-inscripciones">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Archive className="w-5 h-5 text-slate-600" />
          Historial de inscripciones
        </CardTitle>
        <CardDescription>Tus solicitudes de años anteriores</CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        {porAnio.map(({ anio, items }) => {
          const abierto = !!expandido[anio];
          return (
            <div key={anio} className="border-b last:border-b-0 py-2">
              <button
                type="button"
                className="w-full flex items-center justify-between py-2 text-left hover:bg-slate-50 rounded px-2 transition"
                onClick={() => setExpandido(prev => ({ ...prev, [anio]: !prev[anio] }))}
                data-testid={`historial-anio-toggle-${anio}`}
              >
                <span className="font-semibold text-slate-800 flex items-center gap-2">
                  {abierto ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  Convocatoria {anio}
                  <span className="text-xs font-normal text-slate-500">({items.length} solicitud{items.length > 1 ? 'es' : ''})</span>
                </span>
              </button>
              {abierto && (
                <div className="pl-6 pr-2 pb-2 space-y-2">
                  {items.map((s) => {
                    const meta = ESTADO_META_HIST[s.estado] || { label: s.estado || '—', color: 'bg-slate-100 text-slate-800' };
                    const fecha = s.fecha_envio ? new Date(s.fecha_envio).toLocaleDateString('es-AR') : '—';
                    return (
                      <div key={s.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 bg-slate-50 rounded border border-slate-200" data-testid={`historial-item-${s.id}`}>
                        <div className="flex flex-col gap-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge className={`${meta.color} text-xs`}>{meta.label}</Badge>
                            <span className="text-xs text-slate-500">Enviada el {fecha}</span>
                          </div>
                          <span className="text-xs text-slate-600 truncate">ID: {s.id}</span>
                        </div>
                        <div className="flex gap-2 flex-wrap items-center">
                          {s.pdf_path && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 text-xs"
                              onClick={async () => {
                                try {
                                  const r = await axios.get(
                                    `${apiUrl}/auxiliares-justicia/mis-solicitudes/${s.id}/pdf`,
                                    { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' },
                                  );
                                  const url = window.URL.createObjectURL(new Blob([r.data]));
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.download = `auxiliar_${s.id}.pdf`;
                                  link.click();
                                  window.URL.revokeObjectURL(url);
                                } catch (e) {
                                  console.error('No se pudo descargar el PDF', e);
                                }
                              }}
                              data-testid={`btn-descargar-pdf-${s.id}`}
                            >
                              <Download className="w-3.5 h-3.5 mr-1" /> PDF
                            </Button>
                          )}
                          <span className="text-xs text-slate-500 italic">
                            {s.estado === 'aprobada' ? '✉️ Comprobante por email' : ''}
                            {s.estado === 'rechazada' ? `Motivo: ${(s.motivo_rechazo || '—').substring(0, 60)}` : ''}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}


// Constantes específicas del radio judicial de Lomas de Zamora (Art. 40 CPCC).
const LOMAS_MAPA_URL = 'https://customer-assets-agu9un31.emergentagent.net/job_6fd29e97-dc82-4c7e-911a-17c413733f48/artifacts/38vdd2aa_Captura%20de%20pantalla%202026-09-02%20000042.png';
const LOMAS_GOOGLE_MAPS_URL = 'https://www.google.com/maps/d/u/0/edit?mid=1ZeoVvURjHb8BJfouHMr7LMMTu5DKWEs&usp=sharing';
const LOMAS_PERIMETRO_CALLES = 'Uriarte, Carlos Glade, Av. Talleres, Martín Rodríguez, Falucho, Molina Arrotea, General Frías, Garibaldi, Av. Hipólito Irigoyen, 9 de Julio, Pasco, Vélez Sarsfield, Amero, Monseñor Schell, Arenales y Malabia hasta las vías del FFCC Roca.';
const DEPTO_LOMAS = 'Lomas de Zamora';

const TITULOS_HABILITANTES = [
  'Licenciado/a en Trabajo Social',
  'Licenciado/a en Servicio Social',
  'Trabajador Social',
  'Asistente Social'
];

// Departamentos judiciales habilitados para este Colegio Distrital (CATSLZ):
// solo Avellaneda-Lanús y Lomas de Zamora. Se pueden elegir uno o ambos.
const DEPARTAMENTOS_JUDICIALES = [
  'Lomas de Zamora',
  'Avellaneda-Lanús',
];

const MAX_DEPARTAMENTOS = 2;

const DOM_LEGAL_VACIO = { domicilio: '', cp: '', localidad: '', tel: '', tel_codigo_area: '', tel_numero: '' };

// ═════════════════════════════════════════════════════════════════════════
//   Selector de Departamentos Judiciales para CATSLZ (Lomas / Avellaneda-Lanús)
//   Se pueden elegir uno o ambos (máx 2).
// ═════════════════════════════════════════════════════════════════════════
function SelectorDepartamentos({ value = [], onChange, disabled = false }) {
  const toggle = (dep) => {
    const isSelected = value.includes(dep);
    if (isSelected) {
      onChange(value.filter(d => d !== dep));
    } else {
      if (value.length >= MAX_DEPARTAMENTOS) return;
      onChange([...value, dep]);
    }
  };

  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 gap-2"
      data-testid="selector-departamentos"
    >
      {DEPARTAMENTOS_JUDICIALES.map((dep) => {
        const selected = value.includes(dep);
        return (
          <label
            key={dep}
            className={`flex items-center gap-3 border rounded-lg p-3 cursor-pointer transition-colors ${
              selected
                ? 'border-amber-500 bg-amber-50 ring-1 ring-amber-500'
                : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50/40'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            data-testid={`opcion-departamento-${dep}`}
          >
            <Checkbox
              checked={selected}
              onCheckedChange={() => !disabled && toggle(dep)}
              disabled={disabled}
              data-testid={`chk-departamento-${dep}`}
            />
            <span className={`text-sm ${selected ? 'font-semibold text-amber-900' : 'text-gray-800'}`}>
              {dep}
            </span>
          </label>
        );
      })}
    </div>
  );
}

/**
 * Intenta separar un teléfono legacy (formato libre) en (código de área, número).
 * Reglas:
 *  - Extrae dígitos.
 *  - Quita '0' inicial (característica) y '15' móvil viejo.
 *  - Devuelve { codigoArea, numero } si el número resultante tiene 8-12 dígitos.
 *  - Preferimos 2-4 dígitos de código de área (2 si len≤10, 3 si 11, 4 si 12).
 *  - Si no cumple, devolvemos null (el usuario tendrá que recargar).
 */
function parseTelefonoARLegacy(tel) {
  if (!tel) return null;
  let d = String(tel).replace(/\D/g, '');
  if (!d) return null;
  if (d.startsWith('0')) d = d.replace(/^0+/, '');
  // Quitar '15' móvil viejo (después de código área de 2-4 dígitos)
  for (const cut of [2, 3, 4]) {
    if (d.length > cut + 2 && d.slice(cut, cut + 2) === '15') {
      d = d.slice(0, cut) + d.slice(cut + 2);
      break;
    }
  }
  if (d.length < 8 || d.length > 12) return null;
  let ca;
  if (d.length <= 10) ca = 2;
  else if (d.length === 11) ca = 3;
  else ca = 4;
  return { codigoArea: d.slice(0, ca), numero: d.slice(ca) };
}




export default function AuxiliaresJusticiaPage() {
  const navigate = useNavigate();
  const { trabajador, token } = useAuth();

  // Estado del proceso
  const [paso, setPaso] = useState(1); // 1 = formulario, 2 = subir documentos
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [camposFaltantesModal, setCamposFaltantesModal] = useState({ open: false, campos: [] });
  const [inscripcionesAbiertas, setInscripcionesAbiertas] = useState(null);
  const [estadoConvocatoria, setEstadoConvocatoria] = useState(null); // {anio_actuacion, fecha_apertura, fecha_cierre, motivo_cierre, ...}
  const [historial, setHistorial] = useState([]); // [{anio_actuacion, solicitudes: [...]}, ...]
  const [historialLoading, setHistorialLoading] = useState(false);
  const [borrador, setBorrador] = useState(null);

  // Datos del formulario (Paso 1)
  const [formData, setFormData] = useState({
    apellido: '',
    nombres: '',
    nacionalidad: 'Argentina',
    documento: '',
    domicilio_profesional: '',
    cp_profesional: '',
    localidad_profesional: '',
    tel_profesional: '',
    domicilio_real: '',
    tel_real: '',
    // Nuevo (iter129): teléfono del domicilio real dividido visualmente.
    // Cuando ambos están cargados, el submit compone `tel_real` = `${ca}-${num}`.
    tel_real_codigo_area: '',
    tel_real_numero: '',
    email_personal: '',
    titulo_habilitante: '',
    fecha_graduacion: '',
    fuero_civil: false,
    fuero_laboral: false,
    domicilio_electronico_corte: '',
    lugar_fecha_curso_procesal: '',
    documentacion_acompanada: '',
    // Tipo de trámite: 'primera_vez' | 'renovacion'
    tipo_inscripcion: '',
    // Nuevo modelo: array de deptos + mapa de domicilios legales por depto.
    departamentos_judiciales: [],
    domicilios_legales: {}, // { [nombre_depto]: {domicilio, cp, localidad, tel} }
  });

  const [esDistritoLomas, setEsDistritoLomas] = useState(true);
  const [cursoEsteAnio, setCursoEsteAnio] = useState(false);
  // Modal específico del radio judicial de Lomas de Zamora
  const [mapaLomasOpen, setMapaLomasOpen] = useState(false);

  // Archivos (Paso 2)
  // Mapa `{[departamento]: File}` — un formulario firmado por departamento elegido.
  const [firmadosPorDep, setFirmadosPorDep] = useState({});
  const [certificado, setCertificado] = useState(null);
  const [copiaDni, setCopiaDni] = useState(null);
  const [sinCertificadoAun, setSinCertificadoAun] = useState(false);

  // ─── Retro-compatibilidad: hidratar borrador legacy con string sedes_inscripcion ───
  const hidratarBorrador = (b) => {
    const datos = { ...(b.datos_formulario || {}) };
    // Si viene el modelo viejo (sedes_inscripcion string + domicilio_legal plano)
    // → migrarlo en memoria al nuevo modelo.
    if (!Array.isArray(datos.departamentos_judiciales)) {
      const legacyStr = (datos.sedes_inscripcion || '').trim();
      const inferidos = legacyStr
        ? legacyStr.split(/[,;/]/).map(s => s.trim()).filter(Boolean)
            .map(txt => {
              // Match aproximado con los oficiales.
              const low = txt.toLowerCase();
              return DEPARTAMENTOS_JUDICIALES.find(d => d.toLowerCase().includes(low) || low.includes(d.toLowerCase()));
            })
            .filter(Boolean)
            .slice(0, MAX_DEPARTAMENTOS)
        : [];
      datos.departamentos_judiciales = inferidos;
      const legales = {};
      if (inferidos[0]) {
        legales[inferidos[0]] = {
          domicilio: datos.domicilio_legal || '',
          cp: datos.cp_legal || '',
          localidad: datos.localidad_legal || '',
          tel: datos.tel_legal || '',
        };
      }
      datos.domicilios_legales = legales;
    }
    // Migración iter129: si el borrador viejo tiene `tel_real` plano pero no
    // los campos separados, intentamos parsearlo en (cód. área, número).
    // Regla: normalizamos a dígitos y aplicamos la partición estándar AR
    // (2-4 dígitos de código de área + resto). Si no podemos partir, dejamos
    // los separados vacíos y mantenemos el legacy — el usuario tendrá que
    // completarlos.
    if (!datos.tel_real_codigo_area && !datos.tel_real_numero && datos.tel_real) {
      const parsed = parseTelefonoARLegacy(datos.tel_real);
      if (parsed) {
        datos.tel_real_codigo_area = parsed.codigoArea;
        datos.tel_real_numero = parsed.numero;
      } else {
        datos.tel_real_codigo_area = '';
        datos.tel_real_numero = '';
      }
    } else {
      datos.tel_real_codigo_area = datos.tel_real_codigo_area || '';
      datos.tel_real_numero = datos.tel_real_numero || '';
    }
    // iter187: migrar `tel` plano de cada domicilio legal a (cód. área + número)
    // para habilitar normalización → envío por WhatsApp/Ayni.
    if (datos.domicilios_legales && typeof datos.domicilios_legales === 'object') {
      Object.keys(datos.domicilios_legales).forEach((dep) => {
        const dl = datos.domicilios_legales[dep] || {};
        if (!dl.tel_codigo_area && !dl.tel_numero && dl.tel) {
          const parsed = parseTelefonoARLegacy(dl.tel);
          if (parsed) {
            dl.tel_codigo_area = parsed.codigoArea;
            dl.tel_numero = parsed.numero;
          } else {
            dl.tel_codigo_area = '';
            dl.tel_numero = '';
          }
        } else {
          dl.tel_codigo_area = dl.tel_codigo_area || '';
          dl.tel_numero = dl.tel_numero || '';
        }
        datos.domicilios_legales[dep] = dl;
      });
    }
    return datos;
  };

  // Estado de "corrección de solicitud observada": si el usuario tiene una
  // solicitud OBSERVADA, mostramos banner + reutilizamos adjuntos originales.
  const [correccion, setCorreccion] = useState(null); // { motivo, correccion_de, adjuntos_originales }

  // Solicitud vigente (enviada / pendiente_nueva_revision / aprobada) → bloqueamos
  // el formulario y mostramos banner en su lugar.
  const [solicitudVigente, setSolicitudVigente] = useState(null);
  // Dialog para anular envío (pide motivo opcional al usuario)
  const [anularDialog, setAnularDialog] = useState({ open: false, motivo: '', loading: false });

  // Verificar estado de inscripciones y borrador existente
  useEffect(() => {
    const init = async () => {
      try {
        const estadoRes = await axios.get(`${API_URL}/auxiliares-justicia/estado-inscripcion`);
        setInscripcionesAbiertas(estadoRes.data.inscripciones_abiertas);
        setEstadoConvocatoria(estadoRes.data);

        if (token) {
          // 1) Primero, ¿tiene una solicitud OBSERVADA? Si sí, iniciar corrección
          //    (idempotente: el backend devuelve el borrador de corrección existente).
          let solObservada = null;
          let solEnProceso = null;
          try {
            const miSol = await axios.get(`${API_URL}/auxiliares-justicia/mi-solicitud`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const est = miSol.data?.estado;
            if (miSol.data && est === 'observada') {
              solObservada = miSol.data;
              const initR = await axios.post(
                `${API_URL}/auxiliares-justicia/iniciar-correccion`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
              );
              const bCorr = initR.data?.borrador || null;
              if (bCorr) {
                setBorrador(bCorr);
                setFormData(hidratarBorrador(bCorr));
                setEsDistritoLomas(bCorr.es_distrito_lomas ?? true);
                setCursoEsteAnio(bCorr.curso_este_anio ?? false);
                const _punto = solObservada.punto_reinicio_observacion || bCorr.punto_reinicio || 'inicio_formulario';
                setCorreccion({
                  motivo: solObservada.motivo_observacion || bCorr.motivo_observacion || '',
                  correccion_de: bCorr.correccion_de,
                  adjuntos_originales: bCorr.adjuntos_originales || {},
                  punto_reinicio: _punto,
                });
                // iter140: si el admin marcó "carga_adjuntos" saltamos al paso 2
                if (_punto === 'carga_adjuntos') setPaso(2);
              }
            } else if (miSol.data && ['enviada', 'enviado', 'pendiente_nueva_revision', 'aprobada'].includes(est)) {
              // Solicitud vigente: bloquear formulario y mostrar banner.
              solEnProceso = miSol.data;
              setSolicitudVigente(miSol.data);
            }
          } catch (e) {
            console.warn('mi-solicitud fallback:', e?.message || e);
          }

          // 2) Si NO había observada ni solicitud en proceso, buscar borrador pendiente normal.
          if (!solObservada && !solEnProceso) {
            const borradorRes = await axios.get(`${API_URL}/auxiliares-justicia/mi-borrador`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (borradorRes.data) {
              setBorrador(borradorRes.data);
              if (borradorRes.data.estado === 'pendiente_firma') {
                setFormData(hidratarBorrador(borradorRes.data));
                setEsDistritoLomas(borradorRes.data.es_distrito_lomas ?? true);
                setCursoEsteAnio(borradorRes.data.curso_este_anio ?? false);
                setPaso(2);
              } else if (borradorRes.data.estado === 'borrador') {
                setFormData(hidratarBorrador(borradorRes.data));
                setEsDistritoLomas(borradorRes.data.es_distrito_lomas ?? true);
                setCursoEsteAnio(borradorRes.data.curso_este_anio ?? false);
                // Si el borrador es de corrección, también prendemos el banner.
                if (borradorRes.data.correccion_de) {
                  const _puntoBorr = borradorRes.data.punto_reinicio || 'inicio_formulario';
                  setCorreccion({
                    motivo: borradorRes.data.motivo_observacion || '',
                    correccion_de: borradorRes.data.correccion_de,
                    adjuntos_originales: borradorRes.data.adjuntos_originales || {},
                    punto_reinicio: _puntoBorr,
                  });
                  // iter140: idem — saltar al paso de adjuntos.
                  if (_puntoBorr === 'carga_adjuntos') setPaso(2);
                }
              }
            }
          }
        }
      } catch (err) {
        console.error('Error inicializando:', err);
      } finally {
        setLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Pre-cargar datos del trabajador
  useEffect(() => {
    if (trabajador && !borrador) {
      setFormData(prev => ({
        ...prev,
        apellido: trabajador.apellido || '',
        nombres: trabajador.nombre || '',
        documento: trabajador.documento || '',
        email_personal: trabajador.email || ''
      }));
    }
  }, [trabajador, borrador]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Sincronizar `domicilios_legales` con la selección: asegurar entradas para
  // cada departamento seleccionado y quitar sobrantes.
  const handleDepartamentosChange = (nuevos) => {
    setFormData(prev => {
      const legales = { ...(prev.domicilios_legales || {}) };
      // Agregar entradas vacías para nuevos deptos
      nuevos.forEach(d => {
        if (!legales[d]) legales[d] = { ...DOM_LEGAL_VACIO };
      });
      // Sacar entradas de deptos ya no seleccionados
      Object.keys(legales).forEach(k => {
        if (!nuevos.includes(k)) delete legales[k];
      });
      return { ...prev, departamentos_judiciales: nuevos, domicilios_legales: legales };
    });
  };

  const handleLegalCampo = (departamento, campo, valor) => {
    setFormData(prev => ({
      ...prev,
      domicilios_legales: {
        ...(prev.domicilios_legales || {}),
        [departamento]: {
          ...(prev.domicilios_legales?.[departamento] || DOM_LEGAL_VACIO),
          [campo]: valor,
        }
      }
    }));
  };

  // Compone el payload a enviar al backend: mantiene `tel_real` sincronizado
  // como `{ca}-{numero}` (concatenación única para el PDF y retro-compat).
  const buildDatosFormularioPayload = () => {
    const ca = (formData.tel_real_codigo_area || '').trim();
    const num = (formData.tel_real_numero || '').trim();
    const tel_real_compuesto = ca && num ? `${ca}-${num}` : (formData.tel_real || '');
    // Componer `tel` por depto legal (cód. área + número → "ca-num") para
    // que el backend pueda normalizarlo y disparar WhatsApp/Ayni.
    const legalesCompuestos = {};
    Object.entries(formData.domicilios_legales || {}).forEach(([dep, dl]) => {
      const dlCa = (dl?.tel_codigo_area || '').trim();
      const dlNum = (dl?.tel_numero || '').trim();
      legalesCompuestos[dep] = {
        domicilio: dl?.domicilio || '',
        cp: dl?.cp || '',
        localidad: dl?.localidad || '',
        tel: dlCa && dlNum ? `${dlCa}-${dlNum}` : (dl?.tel || ''),
        tel_codigo_area: dlCa,
        tel_numero: dlNum,
      };
    });
    return {
      ...formData,
      tel_real: tel_real_compuesto,
      domicilios_legales: legalesCompuestos,
    };
  };

  // Guardar borrador
  const guardarBorrador = async () => {
    try {
      await axios.post(`${API_URL}/auxiliares-justicia/guardar-borrador`, {
        datos_formulario: buildDatosFormularioPayload(),
        es_distrito_lomas: esDistritoLomas,
        curso_este_anio: cursoEsteAnio
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error('Error guardando borrador:', err);
    }
  };

  // Generar y descargar PDF
  const handleGenerarPDF = async () => {
    setError(null);
    setSubmitting(true);

    const erroresValidacion = [];

    // Datos personales
    if (!formData.apellido?.trim()) erroresValidacion.push('Apellido');
    if (!formData.nombres?.trim()) erroresValidacion.push('Nombres');
    if (!formData.documento?.trim()) erroresValidacion.push('DNI');
    if (!formData.email_personal?.trim()) erroresValidacion.push('Email Personal');

    // Domicilio Profesional
    if (!formData.domicilio_profesional?.trim()) erroresValidacion.push('Dirección Profesional');
    if (!formData.localidad_profesional?.trim()) erroresValidacion.push('Localidad Profesional');
    if (!formData.cp_profesional?.trim()) erroresValidacion.push('CP Profesional');
    if (!formData.tel_profesional?.trim()) erroresValidacion.push('Teléfono Profesional');

    // Domicilio Real
    if (!formData.domicilio_real?.trim()) erroresValidacion.push('Dirección Real');
    if (!formData.tel_real_codigo_area?.trim()) erroresValidacion.push('Código de Área (Domicilio Real)');
    if (!formData.tel_real_numero?.trim()) erroresValidacion.push('Número de Teléfono (Domicilio Real)');
    if (formData.tel_real_codigo_area && formData.tel_real_codigo_area.length < 2) {
      erroresValidacion.push('Código de Área debe tener al menos 2 dígitos');
    }
    if (formData.tel_real_numero && formData.tel_real_numero.length < 6) {
      erroresValidacion.push('Número de Teléfono debe tener al menos 6 dígitos');
    }

    // Formación
    if (!formData.titulo_habilitante) erroresValidacion.push('Título Habilitante');
    if (!formData.fecha_graduacion) erroresValidacion.push('Fecha de Graduación');

    // Inscripción
    if (!formData.tipo_inscripcion) {
      erroresValidacion.push('Tipo de Inscripción');
    }
    if (!formData.fuero_civil && !formData.fuero_laboral) {
      erroresValidacion.push('Al menos un Fuero (Civil o Laboral)');
    }
    if (!formData.domicilio_electronico_corte?.trim()) {
      erroresValidacion.push('Domicilio Electrónico de la Corte');
    }
    // El curso solo se pide en la primera inscripción; en renovación no aplica.
    if (formData.tipo_inscripcion === 'primera_vez' && !formData.lugar_fecha_curso_procesal?.trim()) {
      erroresValidacion.push('Lugar y Fecha del Curso Procesal');
    }
    if (!formData.documentacion_acompanada?.trim()) {
      erroresValidacion.push('Documentación Acompañada');
    }

    // Departamentos judiciales (nuevo modelo)
    const deptos = formData.departamentos_judiciales || [];
    if (deptos.length === 0) {
      erroresValidacion.push('Departamento Judicial (al menos 1)');
    } else if (deptos.length > MAX_DEPARTAMENTOS) {
      erroresValidacion.push(`Máximo ${MAX_DEPARTAMENTOS} departamentos judiciales`);
    }

    // Domicilio Legal por cada depto seleccionado
    deptos.forEach(dep => {
      const dl = formData.domicilios_legales?.[dep] || {};
      if (!dl.domicilio?.trim()) erroresValidacion.push(`Dirección Legal (${dep})`);
      if (!dl.localidad?.trim()) erroresValidacion.push(`Localidad Legal (${dep})`);
      if (!dl.cp?.trim()) erroresValidacion.push(`CP Legal (${dep})`);
      if (!dl.tel_codigo_area?.trim()) erroresValidacion.push(`Código de Área Teléfono Legal (${dep})`);
      if (!dl.tel_numero?.trim()) erroresValidacion.push(`Número de Teléfono Legal (${dep})`);
    });

    if (erroresValidacion.length > 0) {
      setCamposFaltantesModal({ open: true, campos: erroresValidacion });
      setSubmitting(false);
      return;
    }

    try {
      await axios.post(`${API_URL}/auxiliares-justicia/guardar-borrador`, {
        datos_formulario: buildDatosFormularioPayload(),
        es_distrito_lomas: esDistritoLomas,
        curso_este_anio: cursoEsteAnio
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const response = await axios.post(
        `${API_URL}/auxiliares-justicia/generar-pdf-borrador`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Si el backend generó múltiples PDFs, viene un ZIP.
      const ct = response.headers['content-type'] || '';
      const isZip = ct.includes('zip');
      const ext = isZip ? 'zip' : 'pdf';
      const url = window.URL.createObjectURL(new Blob([response.data], { type: ct || (isZip ? 'application/zip' : 'application/pdf') }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `formulario_auxiliar_${formData.apellido}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setPaso(2);

    } catch (err) {
      setError(err.response?.data?.detail || 'Error al generar el PDF');
    } finally {
      setSubmitting(false);
    }
  };

  // Enviar inscripción final
  const handleEnviarInscripcion = async () => {
    setError(null);

    const deptosSel = formData.departamentos_judiciales || [];
    // Si no hay deptos en el borrador (caso edge), exigir al menos 1 firmado.
    const deptosParaFirmar = deptosSel.length > 0 ? deptosSel : ['unico'];
    const faltantes = deptosParaFirmar.filter(d => !firmadosPorDep[d]);
    // En correcciones: si el usuario NO subió nuevos firmados y los originales
    // existen, dejamos pasar (backend los reutilizará).
    const puedeReutilizarFirmados = !!(correccion && correccion.adjuntos_originales?.formularios_firmados_por_departamento?.length);

    const erroresAdjuntos = [];

    if (faltantes.length > 0) {
      const subioAlguno = deptosParaFirmar.length - faltantes.length > 0;
      if (!puedeReutilizarFirmados || subioAlguno) {
        if (deptosSel.length > 1) {
          faltantes.forEach(dep => {
            erroresAdjuntos.push(`Formulario firmado de ${dep}`);
          });
        } else {
          erroresAdjuntos.push('Formulario firmado');
        }
      }
    }

    const puedeReutilizarDni = !!(correccion && correccion.adjuntos_originales?.copia_dni);
    if (!esDistritoLomas && !copiaDni && !puedeReutilizarDni) {
      erroresAdjuntos.push('Copia de DNI (matriculadxs de otros distritos)');
    }

    const esRenovacion = formData.tipo_inscripcion === 'renovacion';
    const puedeReutilizarCert = !!(correccion && correccion.adjuntos_originales?.certificado);
    if (!esRenovacion && !sinCertificadoAun && !certificado && !puedeReutilizarCert) {
      erroresAdjuntos.push('Certificado/acta del curso (o marcar que aún no lo tenés)');
    }

    if (erroresAdjuntos.length > 0) {
      setCamposFaltantesModal({ open: true, campos: erroresAdjuntos });
      return;
    }

    setSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('sin_certificado_aun', sinCertificadoAun);

      // Adjuntar SOLO los firmados que el usuario subió (backend reutiliza los
      // originales si no viene ninguno, en modo corrección).
      const subidos = deptosParaFirmar.filter(d => firmadosPorDep[d]);
      subidos.forEach(dep => {
        formDataToSend.append('formularios_firmados', firmadosPorDep[dep]);
      });
      if (subidos.length > 0) {
        formDataToSend.append('formularios_firmados_deptos', JSON.stringify(subidos));
      }

      // Retro-compat legacy (1 firmado): también lo mandamos como `formulario_firmado`.
      if (subidos.length === 1) {
        formDataToSend.append('formulario_firmado', firmadosPorDep[subidos[0]]);
      }

      if (certificado) formDataToSend.append('certificado', certificado);
      if (copiaDni) formDataToSend.append('copia_dni', copiaDni);

      await axios.post(`${API_URL}/auxiliares-justicia/enviar-v2`, formDataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        // Conexiones móviles + archivos pesados pueden tardar. Damos 120s.
        timeout: 120000,
      });

      setSuccess(true);

    } catch (err) {
      // Timeout específico (axios: code === 'ECONNABORTED' o message con 'timeout').
      const isTimeout = err?.code === 'ECONNABORTED'
        || (typeof err?.message === 'string' && err.message.toLowerCase().includes('timeout'));
      if (isTimeout) {
        setError(
          'La subida está tardando más de lo esperado debido a tu conexión. '
          + 'Por favor, no cierres la página y reintentá. Tus archivos siguen cargados.'
        );
      } else {
        setError(err.response?.data?.detail || 'Error al enviar la inscripción');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Empezar de nuevo
  // iter190: si hay una corrección activa (solicitud observada), ofrecer
  // 2 opciones: (a) resetear formulario conservando el vínculo a la solicitud
  // observada, o (b) cancelar la corrección por completo y arrancar como una
  // inscripción nueva.
  const handleEmpezarDeNuevo = async () => {
    const esCorreccion = Boolean(correccion?.correccion_de);
    let modo = 'reset_total'; // default sin corrección
    if (esCorreccion) {
      const mantener = window.confirm(
        'Estás corrigiendo una solicitud observada.\n\n' +
        '✓ ACEPTAR: resetear todos los campos del formulario pero conservar el vínculo con la solicitud observada (seguirá siendo una CORRECCIÓN).\n' +
        '✗ CANCELAR: podrás elegir si querés descartar la corrección y arrancar como una inscripción nueva.',
      );
      if (mantener) {
        modo = 'reset_conservar_correccion';
      } else {
        const descartar = window.confirm(
          '¿Querés DESCARTAR la corrección y empezar como una inscripción nueva?\n\n' +
          'Esto desvincula el nuevo formulario de la solicitud observada.\n' +
          'Tocá "Cancelar" si querés volver atrás sin cambios.',
        );
        if (!descartar) return;
        modo = 'descartar_correccion';
      }
    } else {
      if (!window.confirm('¿Seguro que querés empezar de nuevo? Se borrará el formulario guardado.')) return;
    }
    try {
      await axios.delete(`${API_URL}/auxiliares-justicia/borrador`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBorrador(null);
      setPaso(1);
      setFormData({
        apellido: trabajador?.apellido || '',
        nombres: trabajador?.nombre || '',
        nacionalidad: 'Argentina',
        documento: trabajador?.documento || '',
        domicilio_profesional: '',
        cp_profesional: '',
        localidad_profesional: '',
        tel_profesional: '',
        domicilio_real: '',
        tel_real: '',
        tel_real_codigo_area: '',
        tel_real_numero: '',
        email_personal: trabajador?.email || '',
        titulo_habilitante: '',
        fecha_graduacion: '',
        fuero_civil: false,
        fuero_laboral: false,
        domicilio_electronico_corte: '',
        lugar_fecha_curso_procesal: '',
        documentacion_acompanada: '',
        tipo_inscripcion: '',
        departamentos_judiciales: [],
        domicilios_legales: {},
      });
      if (modo === 'descartar_correccion') {
        setCorreccion(null);
      }
      // Si el modo es "reset_conservar_correccion" dejamos `correccion` tal cual,
      // así cuando el usuario vuelva a llenar/guardar el formulario, el próximo
      // POST /guardar-borrador reasignará `correccion_de` a este borrador nuevo.
    } catch (err) {
      console.error('Error eliminando borrador:', err);
    }
  };

  // Descargar PDF nuevamente
  const handleDescargarPDFNuevamente = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/auxiliares-justicia/generar-pdf-borrador`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const ct = response.headers['content-type'] || '';
      const isZip = ct.includes('zip');
      const ext = isZip ? 'zip' : 'pdf';
      const url = window.URL.createObjectURL(new Blob([response.data], { type: ct || (isZip ? 'application/zip' : 'application/pdf') }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `formulario_auxiliar_${formData.apellido}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Error al descargar el PDF');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  // Inscripciones cerradas
  // iter190: BYPASS — espejo del guard backend `_validar_convocatoria_abierta`
  // (iter185). Si la usuaria tiene:
  //   (a) una CORRECCIÓN activa (solicitud observada con borrador en curso), o
  //   (b) una solicitud en estado `pendiente_nueva_revision` (ya corrigió y
  //       está esperando la segunda revisión del admin),
  // NO mostramos la pantalla "Convocatoria cerrada". Es la misma salvedad del
  // backend: una matriculada observada dentro de la ventana no puede perder su
  // flujo si el admin cierra la convocatoria antes de que termine de corregir.
  const _tieneFlujoAbiertoPorBypass = Boolean(
    correccion ||
    (solicitudVigente && ['observada', 'pendiente_nueva_revision'].includes(solicitudVigente.estado))
  );
  if (inscripcionesAbiertas === false && !_tieneFlujoAbiertoPorBypass) {
    // iter184: copy nueva solicitada
    // - Título usa anio_actuacion (el año de la convocatoria actual que está cerrada o pendiente)
    // - Cuerpo informa el próximo ciclo (anio_actuacion + 1) y la fecha de apertura 01/09/anio_actuacion
    const anioActuacion = Number(estadoConvocatoria?.anio_actuacion) || null;
    const anioProxCiclo = anioActuacion ? anioActuacion + 1 : null;
    const fechaProxApertura = anioActuacion ? `01/09/${anioActuacion}` : null;

    return (
      <div className="min-h-screen bg-gray-50">
        <UserHeader />
        <div className="max-w-2xl mx-auto px-4 py-12">
          <Card className="border-amber-200" data-testid="banner-convocatoria-cerrada">
            <CardContent className="pt-6 text-center">
              <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
              <h2
                className="text-xl font-bold mb-3"
                data-testid="banner-convocatoria-cerrada-titulo"
              >
                {anioActuacion
                  ? `Inscripción ${anioActuacion}: Convocatoria cerrada.`
                  : 'Convocatoria cerrada'}
              </h2>
              <p className="text-gray-700 mb-3">
                Por disposición de la SCBA la inscripción al listado de Auxiliares de Justicia se
                realiza durante el mes de septiembre de cada año.
              </p>
              {anioProxCiclo && fechaProxApertura && (
                <p
                  className="text-gray-700"
                  data-testid="banner-convocatoria-cerrada-proxima"
                >
                  Las inscripciones para el <strong>{anioProxCiclo}</strong> se abrirán nuevamente
                  el <strong>{fechaProxApertura}</strong>.
                </p>
              )}
            </CardContent>
          </Card>

          {token && (
            <HistorialInscripciones token={token} apiUrl={API_URL} />
          )}
        </div>
        <WhatsAppFAB />
      </div>
    );
  }

  // Éxito
  if (success) {
    return (
      <div className="min-h-screen bg-gray-50">
        <UserHeader />
        <div className="max-w-2xl mx-auto px-4 py-12">
          <Card className="border-green-200">
            <CardContent className="pt-6 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2 text-green-700">¡Inscripción Enviada!</h2>
              <p className="text-gray-600 mb-4">
                Tu solicitud ha sido enviada correctamente. Recibirás un email de confirmación.
              </p>
              <p className="text-sm text-gray-500">
                El Colegio revisará tu documentación y te contactará en caso de ser necesario.
              </p>
              <Button onClick={() => goBack()} className="mt-6">
                Volver
              </Button>
            </CardContent>
          </Card>
        </div>
        <WhatsAppFAB />
      </div>
    );
  }

  // Solicitud vigente: bloqueo por duplicado.
  if (solicitudVigente && ['enviada', 'enviado', 'pendiente_nueva_revision', 'aprobada'].includes(solicitudVigente.estado)) {
    const estado = solicitudVigente.estado;
    const enRevision = estado === 'enviada' || estado === 'enviado';
    const enNuevaRevision = estado === 'pendiente_nueva_revision';
    const aprobada = estado === 'aprobada';
    const fechaEnvio = solicitudVigente.fecha_reenvio || solicitudVigente.fecha_envio || '';
    const fechaAprob = solicitudVigente.fecha_aprobacion || '';
    const anioAprob = fechaAprob ? new Date(fechaAprob).getFullYear() : null;
    const anioActual = new Date().getFullYear();
    const mesActual = new Date().getMonth() + 1;
    const puedeRenovar = aprobada && anioAprob && anioAprob < anioActual && mesActual === 9;

    return (
      <div className="min-h-screen bg-gray-50">
        <UserHeader />
        <div className="max-w-3xl mx-auto px-4 py-10">
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-3 mb-2">
              <Scale className="w-10 h-10 text-amber-600" />
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Auxiliares de Justicia</h1>
            </div>
          </div>

          {aprobada ? (
            <Card className="border-green-200" data-testid="banner-solicitud-aprobada">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-8 h-8 text-green-500 flex-shrink-0" />
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-green-800 mb-1">Tu inscripción está aprobada</h2>
                    <p className="text-sm text-gray-700">
                      El Colegio validó tu solicitud del año {anioAprob || '—'}. No necesitás enviarla nuevamente.
                    </p>
                    {puedeRenovar && (
                      <div className="mt-4 bg-amber-50 border border-amber-200 rounded-md p-3">
                        <p className="text-sm text-amber-900">
                          Estamos en septiembre. Podés iniciar tu <strong>Renovación Anual</strong> para el año en curso.
                        </p>
                        <Button
                          className="mt-3 bg-amber-600 hover:bg-amber-700"
                          data-testid="btn-iniciar-renovacion"
                          onClick={async () => {
                            try {
                              await axios.post(
                                `${API_URL}/auxiliares-justicia/iniciar-renovacion`,
                                {},
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              window.location.reload();
                            } catch (e) {
                              setError(e?.response?.data?.detail || 'No se pudo iniciar la renovación');
                            }
                          }}
                        >
                          Iniciar Renovación Anual
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-blue-200" data-testid="banner-solicitud-en-proceso">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className={`w-8 h-8 flex-shrink-0 ${enNuevaRevision ? 'text-purple-500' : 'text-blue-500'}`} />
                  <div className="flex-1">
                    <h2 className={`text-xl font-bold mb-1 ${enNuevaRevision ? 'text-purple-800' : 'text-blue-800'}`}>
                      Tu solicitud ya está en proceso de revisión
                    </h2>
                    <p className="text-sm text-gray-700">
                      No es necesario enviarla nuevamente. El Colegio se comunicará por email cuando termine la revisión.
                    </p>
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                      <div className="bg-white border rounded-md p-3">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Estado</p>
                        <p className="font-medium text-gray-900" data-testid="estado-solicitud-vigente">
                          {enRevision ? 'Enviada · en revisión' : 'Corregida · en nueva revisión'}
                        </p>
                      </div>
                      <div className="bg-white border rounded-md p-3">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Fecha</p>
                        <p className="font-medium text-gray-900">
                          {fechaEnvio ? new Date(fechaEnvio).toLocaleString('es-AR') : '—'}
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        disabled
                        data-testid="btn-envio-deshabilitado"
                      >
                        Ya enviaste tu solicitud
                      </Button>
                      {enRevision && (
                        <Button
                          variant="outline"
                          className="border-amber-500 text-amber-700 hover:bg-amber-50"
                          data-testid="btn-anular-envio"
                          onClick={() => setAnularDialog({ open: true, motivo: '', loading: false })}
                        >
                          Anular envío y corregir
                        </Button>
                      )}
                    </div>
                    {enRevision && (
                      <p className="text-xs text-gray-500 mt-2">
                        Podés anular solo mientras la solicitud no haya sido revisada por el Colegio.
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Dialog: anular envío con motivo opcional */}
        <Dialog
          open={anularDialog.open}
          onOpenChange={(o) => !anularDialog.loading && setAnularDialog(prev => ({ ...prev, open: o }))}
        >
          <DialogContent data-testid="dialog-anular-envio">
            <DialogHeader>
              <DialogTitle>Anular envío y corregir</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-gray-700">
                Tu solicitud actual quedará <strong>anulada</strong> y volverás al formulario para
                editar los datos y los adjuntos. Deberás volver a enviarla al finalizar.
              </p>
              <div>
                <Label htmlFor="motivo-anulacion" className="text-sm">
                  Motivo <span className="text-gray-400 font-normal">(opcional, queda en el historial)</span>
                </Label>
                <Textarea
                  id="motivo-anulacion"
                  data-testid="input-motivo-anulacion"
                  rows={3}
                  placeholder="Ej: cargué mal el domicilio legal, quiero adjuntar otro certificado, etc."
                  value={anularDialog.motivo}
                  onChange={(e) => setAnularDialog(prev => ({ ...prev, motivo: e.target.value }))}
                  disabled={anularDialog.loading}
                  maxLength={500}
                />
                <p className="text-xs text-gray-400 mt-1">{anularDialog.motivo.length}/500</p>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  disabled={anularDialog.loading}
                  onClick={() => setAnularDialog({ open: false, motivo: '', loading: false })}
                  data-testid="btn-anular-cancelar"
                >
                  Cancelar
                </Button>
                <Button
                  className="bg-amber-600 hover:bg-amber-700"
                  disabled={anularDialog.loading}
                  data-testid="btn-anular-confirmar"
                  onClick={async () => {
                    setAnularDialog(prev => ({ ...prev, loading: true }));
                    try {
                      await axios.post(
                        `${API_URL}/auxiliares-justicia/anular-envio`,
                        { motivo: anularDialog.motivo.trim() },
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      window.location.reload();
                    } catch (e) {
                      setError(e?.response?.data?.detail || 'No se pudo anular el envío');
                      setAnularDialog({ open: false, motivo: '', loading: false });
                    }
                  }}
                >
                  {anularDialog.loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Anulando…</>
                  ) : (
                    'Confirmar anulación'
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        <WhatsAppFAB />
      </div>
    );
  }

  const deptos = formData.departamentos_judiciales || [];
  const legales = formData.domicilios_legales || {};

  return (
    <div className="min-h-screen bg-gray-50">
      <UserHeader />

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Scale className="w-10 h-10 text-amber-600" />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Auxiliares de Justicia
            </h1>
          </div>
          <p className="text-gray-600">
            Inscripción al Listado Oficial según Acordada 2728
          </p>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <div className={`flex items-center gap-2 ${paso >= 1 ? 'text-amber-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${paso >= 1 ? 'bg-amber-600 text-white' : 'bg-gray-200'}`}>
              {paso > 1 ? <CheckCircle className="w-5 h-5" /> : '1'}
            </div>
            <span className="hidden sm:inline font-medium">Completar formulario</span>
          </div>
          <div className="w-12 h-0.5 bg-gray-200" />
          <div className={`flex items-center gap-2 ${paso >= 2 ? 'text-amber-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${paso >= 2 ? 'bg-amber-600 text-white' : 'bg-gray-200'}`}>
              2
            </div>
            <span className="hidden sm:inline font-medium">Subir documentación</span>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" data-testid="alerta-error">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Banner de corrección: si el usuario está corrigiendo una solicitud observada */}
        {correccion && (
          <div className="mb-6 border-l-4 border-amber-500 bg-amber-50 rounded-lg p-5" data-testid="banner-observacion">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-semibold text-amber-900 mb-1">
                  Tu inscripción tiene observaciones que necesitás corregir
                </h3>
                <div className="bg-white/60 rounded-md p-3 border border-amber-200 mt-2">
                  <p className="text-xs font-medium text-amber-800 mb-1 uppercase tracking-wide">Motivo</p>
                  <p className="text-sm text-amber-950 whitespace-pre-wrap" data-testid="motivo-observacion">
                    {correccion.motivo || 'Consultá al colegio para más detalle.'}
                  </p>
                </div>
                <p className="text-sm text-amber-800 mt-3">
                  Ya cargamos tu formulario anterior para que edites lo necesario. En el Paso 2,
                  <strong> los documentos que ya subiste se reutilizarán automáticamente</strong> si no los volvés
                  a cargar. Sólo subí de nuevo aquello que estés corrigiendo.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* PASO 1: Formulario */}
        {paso === 1 && (
          <Card>
            <CardHeader>
              <CardTitle>Paso 1: Completá el formulario</CardTitle>
              <CardDescription>
                Ingresá tus datos. Luego podrás descargar el PDF para firmarlo.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Preguntas clave */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-4">
                <h3 className="font-semibold text-amber-800">Información importante</h3>

                {/* ── Tipo de inscripción (lo primero que se ve) ── */}
                <div>
                  <Label className="mb-2 block">Tipo de Inscripción *</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="select-tipo-inscripcion">
                    {[
                      { value: 'primera_vez', label: 'Inscripción por primera vez', testId: 'opt-primera-vez' },
                      { value: 'renovacion', label: 'Renovación anual (Re-inscripción)', testId: 'opt-renovacion' },
                    ].map(opt => {
                      const selected = formData.tipo_inscripcion === opt.value;
                      return (
                        <label
                          key={opt.value}
                          className={`flex items-center gap-3 border rounded-lg p-3 cursor-pointer transition-colors bg-white ${
                            selected
                              ? 'border-amber-500 ring-1 ring-amber-500'
                              : 'border-amber-200 hover:border-amber-400'
                          }`}
                          data-testid={opt.testId}
                        >
                          <input
                            type="radio"
                            name="tipo_inscripcion"
                            value={opt.value}
                            checked={selected}
                            onChange={() => handleInputChange('tipo_inscripcion', opt.value)}
                            className="w-4 h-4 text-amber-600 flex-shrink-0"
                          />
                          <span className={`text-sm ${selected ? 'font-semibold text-amber-900' : 'text-gray-800'}`}>
                            {opt.label}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  {formData.tipo_inscripcion === 'renovacion' && (
                    <div
                      className="mt-3 bg-white border-l-4 border-amber-500 rounded p-3 flex gap-2 items-start"
                      data-testid="aviso-renovacion"
                    >
                      <Info className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-amber-900 leading-relaxed">
                        Al renovar tu inscripción, ratificás que mantenés tu{' '}
                        <strong>Firma Digital vigente</strong> y tu <strong>matrícula al día</strong>{' '}
                        conforme a la normativa de la SCBA.
                      </p>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>¿Sos matriculade del Colegio de TS de Lomas de Zamora?</Label>
                  <div className="flex gap-4 flex-wrap">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={esDistritoLomas}
                        onChange={() => setEsDistritoLomas(true)}
                        className="w-4 h-4 text-amber-600"
                        data-testid="radio-matriculade-catslz-si"
                      />
                      <span>Sí, soy matriculade del CATSLZ</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={!esDistritoLomas}
                        onChange={() => setEsDistritoLomas(false)}
                        className="w-4 h-4 text-amber-600"
                        data-testid="radio-matriculade-catslz-no"
                      />
                      <span>No, soy de otro Colegio Distrital</span>
                    </label>
                  </div>
                  <p className="text-xs italic text-gray-600 mt-1" data-testid="aclaracion-matriculacion-vs-departamento">
                    Esta pregunta refiere a tu lugar de matriculación profesional, no al departamento
                    judicial donde deseás inscribirte.
                  </p>
                  {!esDistritoLomas && (
                    <p className="text-sm text-amber-700 mt-1">
                      <Info className="w-4 h-4 inline mr-1" />
                      Deberás adjuntar copia de DNI junto con el formulario firmado.
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>¿Realizaste el curso de capacitación procesal este año?</Label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={cursoEsteAnio}
                        onChange={() => setCursoEsteAnio(true)}
                        className="w-4 h-4 text-amber-600"
                      />
                      <span>Sí, este año</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={!cursoEsteAnio}
                        onChange={() => setCursoEsteAnio(false)}
                        className="w-4 h-4 text-amber-600"
                      />
                      <span>No, en años anteriores</span>
                    </label>
                  </div>
                  {cursoEsteAnio && (
                    <p className="text-sm text-amber-700 mt-1">
                      <Info className="w-4 h-4 inline mr-1" />
                      Si aún no te emitieron el certificado, podrás indicarlo en el paso 2 y enviar solo el formulario firmado.
                    </p>
                  )}
                </div>
              </div>

              {/* Datos personales */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Apellido *</Label>
                  <Input value={formData.apellido} onChange={e => handleInputChange('apellido', e.target.value)} />
                </div>
                <div>
                  <Label>Nombres *</Label>
                  <Input value={formData.nombres} onChange={e => handleInputChange('nombres', e.target.value)} />
                </div>
                <div>
                  <Label>Nacionalidad</Label>
                  <Input value={formData.nacionalidad} onChange={e => handleInputChange('nacionalidad', e.target.value)} />
                </div>
                <div>
                  <Label>DNI *</Label>
                  <Input value={formData.documento} onChange={e => handleInputChange('documento', e.target.value)} />
                </div>
              </div>

              {/* Aclaración general sobre domicilios */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3" data-testid="aviso-domicilios">
                <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-900 leading-relaxed space-y-1.5">
                  <p>
                    <strong>Regla clave:</strong> el domicilio <strong>Legal</strong> debe respetar
                    la jurisdicción del departamento judicial en el que te inscribís (Resolución SCBA).
                    Los tres domicilios (<strong>Real</strong>, <strong>Profesional</strong> y{' '}
                    <strong>Legal</strong>) pueden coincidir entre sí siempre que el Legal cumpla
                    con la jurisdicción exigida:
                  </p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>
                      <strong>Lomas de Zamora:</strong> el domicilio legal debe estar dentro del{' '}
                      <button
                        type="button"
                        onClick={() => setMapaLomasOpen(true)}
                        className="inline items-baseline font-semibold text-amber-800 underline decoration-dotted underline-offset-2 hover:text-amber-950 hover:decoration-solid"
                        data-testid="link-radio-departamental"
                      >
                        radio de Tribunales (ver mapa)
                      </button>.
                    </li>
                    <li>
                      <strong>Avellaneda-Lanús:</strong> el domicilio legal debe estar dentro de los partidos de
                      <strong> Avellaneda</strong> o <strong>Lanús</strong>.
                    </li>
                  </ul>
                  <p className="text-xs pt-1">
                    Si te inscribís en los dos departamentos, cada formulario lleva su propio domicilio legal cumpliendo el criterio de esa jurisdicción.
                  </p>
                </div>
              </div>

              {/* Domicilio Profesional */}
              <div>
                <h3 className="font-semibold mb-1">Domicilio Profesional *</h3>
                <details className="mb-3 group" data-testid="info-domicilio-profesional">
                  <summary className="cursor-pointer select-none text-sm font-medium text-amber-700 hover:text-amber-800 inline-flex items-center gap-1.5 list-none">
                    <Info className="w-4 h-4 flex-shrink-0" />
                    <span>Más información</span>
                    <span className="text-xs text-amber-600 group-open:hidden">(mostrar)</span>
                    <span className="text-xs text-amber-600 hidden group-open:inline">(ocultar)</span>
                  </summary>
                  <p className="mt-2 text-sm text-gray-700 leading-relaxed bg-amber-50 border border-amber-200 rounded-md p-3">
                    Es el domicilio desde el cual ejercés tu profesión: sede, oficina o consultorio.
                    Debe estar ubicado en alguno de los seis municipios del CATSLZ
                    (Lomas de Zamora, Almirante Brown, Esteban Echeverría, Ezeiza, Avellaneda o Lanús).
                    No es obligatorio consignar el domicilio de tu empleador.
                    Podés poner tu domicilio real (si está dentro de alguno de los municipios mencionados anteriormente).
                    <br /><br />
                    <strong>Coincidencias:</strong> puede coincidir únicamente con el domicilio <strong>Real</strong>.
                    No puede coincidir con el <strong>Legal</strong>, salvo que este último cumpla con el
                    radio departamental que exige la SCBA.
                  </p>
                </details>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label>Dirección *</Label>
                    <Input value={formData.domicilio_profesional} onChange={e => handleInputChange('domicilio_profesional', e.target.value)} placeholder="Calle y número" data-testid="input-domicilio-profesional" />
                  </div>
                  <div>
                    <Label>Localidad *</Label>
                    <Input value={formData.localidad_profesional} onChange={e => handleInputChange('localidad_profesional', e.target.value)} />
                  </div>
                  <div>
                    <Label>CP *</Label>
                    <Input value={formData.cp_profesional} onChange={e => handleInputChange('cp_profesional', e.target.value)} />
                  </div>
                  <div>
                    <Label>Teléfono *</Label>
                    <Input value={formData.tel_profesional} onChange={e => handleInputChange('tel_profesional', e.target.value)} />
                  </div>
                </div>
              </div>

              {/* Domicilio Real */}
              <div>
                <h3 className="font-semibold mb-1">Domicilio Real *</h3>
                <details className="mb-3 group" data-testid="info-domicilio-real">
                  <summary className="cursor-pointer select-none text-sm font-medium text-amber-700 hover:text-amber-800 inline-flex items-center gap-1.5 list-none">
                    <Info className="w-4 h-4 flex-shrink-0" />
                    <span>Más información</span>
                    <span className="text-xs text-amber-600 group-open:hidden">(mostrar)</span>
                    <span className="text-xs text-amber-600 hidden group-open:inline">(ocultar)</span>
                  </summary>
                  <p className="mt-2 text-sm text-gray-700 leading-relaxed bg-amber-50 border border-amber-200 rounded-md p-3">
                    Es tu domicilio particular, donde vivís efectivamente. Puede estar fuera de los
                    municipios del CATSLZ y de los departamentos judiciales en los que solicitás inscripción.
                    <br /><br />
                    <strong>Coincidencias:</strong> puede coincidir únicamente con el domicilio <strong>Profesional</strong>.
                    No puede coincidir con el <strong>Legal</strong>, salvo que este último cumpla con el
                    radio departamental que exige la SCBA.
                  </p>
                </details>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label>Dirección *</Label>
                    <Input value={formData.domicilio_real} onChange={e => handleInputChange('domicilio_real', e.target.value)} placeholder="Calle y número" />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Teléfono / Celular *</Label>
                    <div className="grid grid-cols-[1fr_2fr] gap-2">
                      <div>
                        <Input
                          type="tel"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={4}
                          value={formData.tel_real_codigo_area}
                          onChange={e => handleInputChange('tel_real_codigo_area', e.target.value.replace(/\D/g, '').slice(0, 4))}
                          placeholder="Cód. Área (sin 0)"
                          data-testid="input-tel-real-codigo-area"
                        />
                        <p className="text-[11px] text-gray-500 mt-1">Ej: 11, 221, 2352</p>
                      </div>
                      <div>
                        <Input
                          type="tel"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={9}
                          value={formData.tel_real_numero}
                          onChange={e => handleInputChange('tel_real_numero', e.target.value.replace(/\D/g, '').slice(0, 9))}
                          placeholder="Número (sin 15)"
                          data-testid="input-tel-real-numero"
                        />
                        <p className="text-[11px] text-gray-500 mt-1">Sólo dígitos, sin espacios ni guiones</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Email */}
              <div>
                <Label>Email Personal *</Label>
                <Input type="email" value={formData.email_personal} onChange={e => handleInputChange('email_personal', e.target.value)} />
              </div>

              {/* Formación */}
              <div>
                <h3 className="font-semibold mb-3">Formación</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Título Habilitante *</Label>
                    <Select value={formData.titulo_habilitante} onValueChange={v => handleInputChange('titulo_habilitante', v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar título" />
                      </SelectTrigger>
                      <SelectContent>
                        {TITULOS_HABILITANTES.map(t => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Fecha de Graduación *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.fecha_graduacion ? (
                            format(parse(formData.fecha_graduacion, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy')
                          ) : (
                            <span className="text-muted-foreground">Seleccionar fecha</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-white border shadow-lg z-50" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.fecha_graduacion ? parse(formData.fecha_graduacion, 'yyyy-MM-dd', new Date()) : undefined}
                          onSelect={(date) => handleInputChange('fecha_graduacion', date ? format(date, 'yyyy-MM-dd') : '')}
                          initialFocus
                          captionLayout="dropdown-buttons"
                          fromYear={1960}
                          toYear={new Date().getFullYear()}
                          classNames={{
                            caption_dropdowns: "flex gap-2",
                            dropdown: "bg-white border rounded px-2 py-1 text-sm",
                            dropdown_month: "bg-white",
                            dropdown_year: "bg-white",
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </div>

              {/* Inscripción: fueros + depto electrónico + curso */}
              <div>
                <h3 className="font-semibold mb-3">Inscripción</h3>
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox checked={formData.fuero_civil} onCheckedChange={v => handleInputChange('fuero_civil', v)} />
                      <span>Fuero Civil *</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox checked={formData.fuero_laboral} onCheckedChange={v => handleInputChange('fuero_laboral', v)} />
                      <span>Fuero Laboral *</span>
                    </label>
                  </div>
                  <div>
                    <Label>Domicilio Electrónico (otorgado por la Corte) *</Label>
                    <Input
                      value={formData.domicilio_electronico_corte}
                      onChange={e => handleInputChange('domicilio_electronico_corte', e.target.value)}
                      placeholder="Ej: 12345678@cts.notificaciones"
                    />
                  </div>
                  {/* Curso de capacitación procesal: solo cuando es primera vez */}
                  {formData.tipo_inscripcion === 'primera_vez' && (
                    <div data-testid="campo-curso-procesal">
                      <Label>Lugar y Fecha del Curso de Capacitación Procesal *</Label>
                      <Input value={formData.lugar_fecha_curso_procesal} onChange={e => handleInputChange('lugar_fecha_curso_procesal', e.target.value)} />
                    </div>
                  )}

                  {/* ── Departamentos judiciales (multiselect hasta 2) ── */}
                  <div>
                    <Label>Departamentos Judiciales donde solicitás inscripción * <span className="text-xs font-normal text-gray-500">(podés elegir uno o los dos)</span></Label>
                    <SelectorDepartamentos
                      value={deptos}
                      onChange={handleDepartamentosChange}
                    />
                    <p className="text-xs text-amber-700 mt-2 bg-amber-50 border border-amber-200 rounded-md p-2 leading-relaxed">
                      <strong>Importante:</strong> este Colegio Distrital (CATSLZ) sólo habilita la inscripción
                      en los departamentos judiciales de <strong>Lomas de Zamora</strong> y/o
                      <strong> Avellaneda-Lanús</strong>. Podés elegir uno o ambos: si elegís los dos,
                      el sistema generará un formulario por cada departamento, compartiendo la misma
                      documentación adjunta.
                    </p>
                  </div>

                  {/* ── Domicilio Legal por departamento (dinámico) ── */}
                  {deptos.length > 0 && (
                    <div className="border-l-4 border-amber-500 pl-4 space-y-6" data-testid="bloque-domicilios-legales">
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {deptos.length === 1 ? 'Domicilio Legal *' : 'Domicilios Legales por Departamento *'}
                        </h3>
                        <details className="mt-1 group" data-testid="info-domicilio-legal">
                          <summary className="cursor-pointer select-none text-sm font-medium text-amber-700 hover:text-amber-800 inline-flex items-center gap-1.5 list-none">
                            <Info className="w-4 h-4 flex-shrink-0" />
                            <span>Más información</span>
                            <span className="text-xs text-amber-600 group-open:hidden">(mostrar)</span>
                            <span className="text-xs text-amber-600 hidden group-open:inline">(ocultar)</span>
                          </summary>
                          <p className="mt-2 text-sm text-gray-700 leading-relaxed bg-amber-50 border border-amber-200 rounded-md p-3">
                            El <strong>Domicilio Legal</strong> es donde el/la auxiliar recibe las notificaciones
                            en su carácter de perito. Por <strong>Resolución de la SCBA</strong> debe
                            constituirse <strong>dentro del radio del departamento judicial</strong> en el que
                            solicitás la inscripción.
                            {deptos.length > 1 && (
                              <> Como elegiste {deptos.length} departamentos, debés declarar un domicilio legal por cada uno.</>
                            )}
                            <br /><br />
                            <strong>Coincidencias:</strong> solo el domicilio <strong>Real</strong> y el
                            <strong> Profesional</strong> pueden coincidir entre sí. El Legal solo podrá
                            coincidir con ellos si cumple con el radio departamental exigido.
                          </p>
                        </details>
                      </div>

                      {deptos.map((dep) => {
                        const dl = legales[dep] || DOM_LEGAL_VACIO;
                        return (
                          <div key={dep} className="bg-amber-50/40 border border-amber-200 rounded-lg p-4 space-y-3" data-testid={`legal-block-${dep}`}>
                            <div className="flex items-center gap-2 flex-wrap">
                              <Scale className="w-4 h-4 text-amber-700" />
                              <p className="font-medium text-amber-900">
                                Domicilio Legal para <span className="underline decoration-amber-400 underline-offset-2">{dep}</span>
                              </p>
                              {dep === DEPTO_LOMAS && (
                                <button
                                  type="button"
                                  onClick={() => setMapaLomasOpen(true)}
                                  className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-amber-700 hover:text-amber-900 underline decoration-dotted underline-offset-2"
                                  data-testid="btn-abrir-mapa-lomas"
                                >
                                  <MapPin className="w-3.5 h-3.5" />
                                  Ver radio judicial (mapa)
                                </button>
                              )}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div className="md:col-span-2">
                                <Label className="text-xs">Dirección *</Label>
                                <Input
                                  value={dl.domicilio}
                                  onChange={e => handleLegalCampo(dep, 'domicilio', e.target.value)}
                                  placeholder="Calle y número"
                                  data-testid={`input-legal-domicilio-${dep}`}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Localidad *</Label>
                                <Input
                                  value={dl.localidad}
                                  onChange={e => handleLegalCampo(dep, 'localidad', e.target.value)}
                                  data-testid={`input-legal-localidad-${dep}`}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">CP *</Label>
                                <Input
                                  value={dl.cp}
                                  onChange={e => handleLegalCampo(dep, 'cp', e.target.value)}
                                  data-testid={`input-legal-cp-${dep}`}
                                />
                              </div>
                              <div className="md:col-span-2">
                                <Label className="text-xs">Teléfono *</Label>
                                <p className="text-[11px] text-gray-500 mb-1">
                                  Para que el Colegio pueda avisarte novedades por WhatsApp / Ayni.
                                </p>
                                <div className="grid grid-cols-[1fr_2fr] gap-2">
                                  <div>
                                    <Input
                                      type="tel"
                                      inputMode="numeric"
                                      pattern="[0-9]*"
                                      maxLength={4}
                                      value={dl.tel_codigo_area || ''}
                                      onChange={e => handleLegalCampo(dep, 'tel_codigo_area', e.target.value.replace(/\D/g, '').slice(0, 4))}
                                      placeholder="Cód. Área"
                                      data-testid={`input-legal-tel-ca-${dep}`}
                                    />
                                    <p className="text-[10px] text-gray-500 mt-1">Sin 0. Ej: 11, 221</p>
                                  </div>
                                  <div>
                                    <Input
                                      type="tel"
                                      inputMode="numeric"
                                      pattern="[0-9]*"
                                      maxLength={9}
                                      value={dl.tel_numero || ''}
                                      onChange={e => handleLegalCampo(dep, 'tel_numero', e.target.value.replace(/\D/g, '').slice(0, 9))}
                                      placeholder="Número (sin 15)"
                                      data-testid={`input-legal-tel-num-${dep}`}
                                    />
                                    <p className="text-[10px] text-gray-500 mt-1">Sólo dígitos</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                            {dep === DEPTO_LOMAS && (
                              <p
                                className="text-[11px] text-red-600 leading-snug pt-1"
                                data-testid="aviso-radio-lomas"
                              >
                                <strong>Recordá</strong> que para este depto judicial, el domicilio legal debe estar dentro del radio de Tribunales (
                                <button
                                  type="button"
                                  onClick={() => setMapaLomasOpen(true)}
                                  className="underline hover:no-underline font-semibold"
                                >
                                  ver mapa
                                </button>
                                ).
                              </p>
                            )}
                            {dep === 'Avellaneda-Lanús' && (
                              <p
                                className="text-[11px] text-red-600 leading-snug pt-1"
                                data-testid="aviso-avellaneda-lanus"
                              >
                                <strong>Recordá</strong> que para este depto judicial, el domicilio legal debe estar dentro de los partidos de <strong>Avellaneda</strong> o <strong>Lanús</strong>.
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div>
                    <Label>Documentación acompañada *</Label>
                    <textarea
                      className="w-full min-h-[80px] p-3 border rounded-md text-sm resize-y focus:outline-none focus:ring-2 focus:ring-amber-500"
                      value={formData.documentacion_acompanada}
                      onChange={e => handleInputChange('documentacion_acompanada', e.target.value)}
                      placeholder="Detallá la documentación que adjuntás..."
                    />
                  </div>
                </div>
              </div>

              {/* Botón generar PDF */}
              <div className="flex flex-col sm:flex-row sm:justify-end gap-2 pt-4 border-t">
                <Button
                  variant="ghost"
                  onClick={handleEmpezarDeNuevo}
                  disabled={submitting}
                  data-testid="btn-empezar-de-nuevo"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {correccion?.correccion_de ? 'Reiniciar / Descartar corrección' : 'Empezar de nuevo'}
                </Button>
                <Button variant="outline" onClick={guardarBorrador} disabled={submitting} data-testid="btn-guardar-borrador">
                  <Upload className="w-4 h-4 mr-2" /> Guardar borrador
                </Button>
                <Button
                  onClick={handleGenerarPDF}
                  disabled={submitting}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="btn-generar-pdf"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generando...</>
                  ) : (
                    <><Download className="w-4 h-4 mr-2" /> Generar y Descargar {deptos.length > 1 ? `${deptos.length} PDFs` : 'PDF'}</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* PASO 2: Subir documentación */}
        {paso === 2 && (
          <Card>
            <CardHeader>
              <CardTitle>Paso 2: Subir documentación firmada</CardTitle>
              <CardDescription>
                {deptos.length > 1 ? (
                  <>Se generó un formulario por cada departamento judicial elegido. Imprimilos, firmalos de puño y letra, y <strong>subí cada uno por separado</strong> en su casillero. No hace falta que los combines en un único PDF.</>
                ) : (
                  <>Imprimí el PDF, firmalo de puño y letra, y subilo junto con la documentación requerida.</>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Aviso: te enviamos el mail con los PDFs */}
              {(formData.email_personal || '').trim() && (
                <div
                  className="flex items-start gap-3 rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900"
                  data-testid="aviso-mail-pdfs"
                >
                  <Mail className="w-5 h-5 flex-shrink-0 mt-0.5 text-sky-600" />
                  <div>
                    Te enviamos también los {deptos.length > 1 ? `${deptos.length} PDFs` : 'PDF'} por
                    correo a <strong>{formData.email_personal}</strong> con las instrucciones para
                    imprimir, firmar y volver a subirlos. Revisá tu bandeja de entrada (y spam,
                    por las dudas). El asunto es:
                    <em className="ml-1">Tus formularios de Auxiliar de Justicia para firmar - CATSLZ</em>.
                  </div>
                </div>
              )}

              {/* Resumen */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold mb-2">Resumen de tu inscripción</h3>
                <div className="text-sm text-gray-600 space-y-1">
                  <p><strong>Nombre:</strong> {formData.apellido}, {formData.nombres}</p>
                  <p><strong>DNI:</strong> {formData.documento}</p>
                  <p><strong>Distrito:</strong> {esDistritoLomas ? 'Lomas de Zamora' : 'Otro distrito'}</p>
                  <p><strong>Curso:</strong> {cursoEsteAnio ? 'Realizado este año' : 'Años anteriores'}</p>
                  <p><strong>Departamento(s) Judicial(es):</strong> {deptos.join(' · ') || '—'}</p>
                </div>
                <div className="flex flex-col sm:flex-row flex-wrap gap-2 mt-3">
                  <Button variant="outline" size="sm" onClick={handleDescargarPDFNuevamente} data-testid="btn-descargar-pdf-nuevamente" className="w-full sm:w-auto justify-center">
                    <Download className="w-4 h-4 mr-1" /> Descargar {deptos.length > 1 ? 'ZIP con PDFs' : 'PDF'} nuevamente
                  </Button>
                  <Button variant="ghost" size="sm" onClick={handleEmpezarDeNuevo} className="w-full sm:w-auto justify-center">
                    <RefreshCw className="w-4 h-4 mr-1" /> Empezar de nuevo
                  </Button>
                </div>
              </div>

              {/* Documentación requerida */}
              <div className="space-y-4">
                <h3 className="font-semibold">Documentación requerida</h3>

                {/* ── Formulario firmado (uno por departamento) ── */}
                {(deptos.length > 0 ? deptos : ['unico']).map((dep) => {
                  const archivo = firmadosPorDep[dep];
                  const label = deptos.length > 1
                    ? <>Formulario firmado — <span className="text-amber-700">{dep}</span> *</>
                    : <>Formulario firmado de puño y letra *</>;
                  // ¿Hay un firmado original guardado para este departamento? (modo corrección)
                  const originalDep = (correccion?.adjuntos_originales?.formularios_firmados_por_departamento || [])
                    .find(o => (o.departamento || 'unico') === dep);
                  return (
                    <div key={dep} className="border rounded-lg p-4" data-testid={`bloque-firmado-${dep}`}>
                      <Label className="text-base font-medium block">{label}</Label>
                      {originalDep && !archivo && (
                        <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded p-2 mt-1" data-testid={`reutilizado-firmado-${dep}`}>
                          ✓ Ya tenemos tu firmado anterior (<strong>{originalDep.filename}</strong>). Se reutilizará si no subís uno nuevo.
                        </p>
                      )}
                      <p className="text-sm text-gray-500 mb-2 mt-2">
                        Subí el PDF que descargaste, imprimiste y firmaste{deptos.length > 1 && <> correspondiente a <strong>{dep}</strong></>}.
                        {originalDep && !archivo && <> Sólo hacelo si estás corrigiendo esta firma.</>}
                      </p>
                      <Input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.webp"
                        onChange={e => {
                          const f = e.target.files[0];
                          setFirmadosPorDep(prev => ({ ...prev, [dep]: f || undefined }));
                        }}
                        data-testid={`input-firmado-${dep}`}
                      />
                      {archivo && (
                        <p className="text-sm text-green-600 mt-1" data-testid={`filename-firmado-${dep}`}>
                          ✓ {archivo.name}
                        </p>
                      )}
                    </div>
                  );
                })}

                {/* Certificado (opcional en Renovación; obligatorio en Primera vez salvo excepción) */}
                {(() => {
                  const esRenov = formData.tipo_inscripcion === 'renovacion';
                  return (
                    <div className="border rounded-lg p-4" data-testid="bloque-certificado">
                      <Label className="text-base font-medium">
                        Certificado o Acta del Curso de Capacitación Procesal
                        {!esRenov && !sinCertificadoAun && ' *'}
                        {esRenov && (
                          <span className="ml-2 text-xs font-normal text-gray-500">(opcional en Renovación)</span>
                        )}
                      </Label>

                      {esRenov && (
                        <p className="text-sm text-gray-600 mt-1" data-testid="cert-opcional-renovacion">
                          Como estás renovando, no es obligatorio adjuntar el certificado nuevamente. Podés subirlo igual si querés dejarlo asentado.
                        </p>
                      )}

                      {!esRenov && cursoEsteAnio && (
                        <div className="my-3 p-3 bg-amber-50 rounded-lg">
                          <label className="flex items-start gap-2 cursor-pointer">
                            <Checkbox
                              checked={sinCertificadoAun}
                              onCheckedChange={v => {
                                setSinCertificadoAun(v);
                                if (v) setCertificado(null);
                              }}
                            />
                            <span className="text-sm text-amber-800">
                              Hice el curso este año y aún no me emitieron el certificado.
                              Me inscribo hasta tanto me lo envíen desde el Consejo Superior.
                            </span>
                          </label>
                        </div>
                      )}

                      {(esRenov || !sinCertificadoAun) && (
                        <>
                          {correccion?.adjuntos_originales?.certificado && !certificado && (
                            <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded p-2 mt-1" data-testid="reutilizado-certificado">
                              ✓ Ya tenemos tu certificado anterior
                              {correccion.adjuntos_originales.certificado_nombre && <> (<strong>{correccion.adjuntos_originales.certificado_nombre}</strong>)</>}.
                              Se reutilizará si no subís uno nuevo.
                            </p>
                          )}
                          <p className="text-sm text-gray-500 mb-2 mt-2">
                            {esRenov
                              ? 'Solo si querés subir uno actualizado (no requerido)'
                              : (cursoEsteAnio
                                  ? 'Podés subir el Acta de Aprobación si ya la tenés'
                                  : 'Subí el certificado del curso')}
                          </p>
                          <Input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png,.webp"
                            onChange={e => setCertificado(e.target.files[0])}
                            data-testid="input-certificado"
                          />
                          {certificado && (
                            <p className="text-sm text-green-600 mt-1">✓ {certificado.name}</p>
                          )}
                        </>
                      )}
                    </div>
                  );
                })()}

                {/* DNI (solo otros distritos) */}
                {!esDistritoLomas && (
                  <div className="border rounded-lg p-4 border-amber-200 bg-amber-50/50">
                    <Label className="text-base font-medium">
                      Copia de DNI *
                    </Label>
                    {correccion?.adjuntos_originales?.copia_dni && !copiaDni && (
                      <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded p-2 mt-1" data-testid="reutilizado-dni">
                        ✓ Ya tenemos tu DNI anterior
                        {correccion.adjuntos_originales.copia_dni_nombre && <> (<strong>{correccion.adjuntos_originales.copia_dni_nombre}</strong>)</>}.
                        Se reutilizará si no subís uno nuevo.
                      </p>
                    )}
                    <p className="text-sm text-gray-500 mb-2 mt-2">
                      Requerido para matriculadxs de otros distritos
                    </p>
                    <Input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.webp"
                      onChange={e => setCopiaDni(e.target.files[0])}
                    />
                    {copiaDni && (
                      <p className="text-sm text-green-600 mt-1">✓ {copiaDni.name}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Botones */}
              <div className="flex justify-between pt-4 border-t">
                {/* iter140: si el admin marcó "carga_adjuntos", el paso 1 queda
                    bloqueado (el PDF original sigue vigente). Ocultamos el botón. */}
                {correccion?.punto_reinicio === 'carga_adjuntos' ? (
                  <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 max-w-md">
                    <strong>Corrección acotada:</strong> el Colegio pidió reemplazar solamente los archivos. El resto del formulario ya fue validado y el PDF firmado sigue siendo válido.
                  </div>
                ) : (
                  <Button variant="outline" onClick={() => setPaso(1)} data-testid="btn-volver-formulario">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Volver al formulario
                  </Button>
                )}
                <Button
                  onClick={handleEnviarInscripcion}
                  disabled={submitting}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="btn-enviar-inscripcion"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando...</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" /> Enviar Inscripción</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Modal: Radio Judicial de Lomas de Zamora (Art. 40 CPCC) */}
      <Dialog
        open={camposFaltantesModal.open}
        onOpenChange={(o) => setCamposFaltantesModal(prev => ({ ...prev, open: o }))}
      >
        <DialogContent className="max-w-lg" data-testid="modal-campos-faltantes">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              Faltan datos obligatorios
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              {paso === 2
                ? 'Antes de enviar la inscripción, adjuntá lo siguiente:'
                : 'Antes de generar los PDFs, completá los siguientes campos:'}
            </p>
            <div
              className="max-h-72 overflow-y-auto rounded-lg border border-red-200 bg-red-50 p-4"
              data-testid="lista-campos-faltantes"
            >
              <ul className="list-disc list-inside space-y-1 text-sm text-red-900">
                {camposFaltantesModal.campos.map((campo, idx) => (
                  <li key={idx} data-testid={`campo-faltante-${idx}`}>{campo}</li>
                ))}
              </ul>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={() => {
                  setCamposFaltantesModal({ open: false, campos: [] });
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="btn-cerrar-campos-faltantes"
              >
                Entendido
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={mapaLomasOpen} onOpenChange={setMapaLomasOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="modal-radio-lomas">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-800">
              <MapPin className="w-5 h-5" />
              Radio Judicial Obligatorio — Lomas de Zamora
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg overflow-hidden border border-amber-200 bg-amber-50">
              <img
                src={LOMAS_MAPA_URL}
                alt="Mapa del radio judicial de Lomas de Zamora"
                className="w-full h-auto block"
                data-testid="img-mapa-lomas"
              />
            </div>
            <div className="bg-amber-50 border-l-4 border-amber-500 rounded p-4">
              <p className="text-sm font-semibold text-amber-900 mb-2">
                Perímetro exigido — Art. 40 del CPCC
              </p>
              <p className="text-sm text-amber-950 leading-relaxed">
                Según el <strong>Art. 40 del CPCC</strong>, el domicilio debe fijarse entre las calles:{' '}
                <em>{LOMAS_PERIMETRO_CALLES}</em>
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-gray-600">
                Si tu domicilio queda fuera de este perímetro, tu inscripción puede ser observada.
              </p>
              <a
                href={LOMAS_GOOGLE_MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors"
                data-testid="btn-google-maps-lomas"
              >
                <ExternalLink className="w-4 h-4" />
                Ver mapa interactivo en Google Maps
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <WhatsAppFAB />
    </div>
  );
}
